import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Params } from "react-router-dom";

import { DataType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";

import { PathParams } from "@/services/api";
import { createSelectByIds } from "@/store/data/data";

interface QueryState {
  isLoading: boolean;
  isLoaded: boolean;
  error?: AppError;
  ids?: Array<string>;
}

// Going to include listAll here because we might need it later, for companies list for example
// but right now it's more work and not required

export enum QueryType {
  GetOne = "getOne",
  List = "list",
  ListAll = "listAll"
}

export interface QueriesState {
  queries: Record<string, QueryState>;
}

interface RequestPayload {
  type: QueryType;
  dataType: DataType;
  params: Params;
  pathParams: PathParams;
}

interface RequestSuccessPayload extends RequestPayload {
  ids: Array<string>;
}

export type QueryAction = PayloadAction<RequestPayload>;
export type QueryStartAction = PayloadAction<RequestPayload>;
export type QuerySuccessAction = PayloadAction<RequestSuccessPayload>;
export type QueryFailureAction = PayloadAction<
  RequestPayload,
  string,
  never,
  AppError
>;

const name = "queries";

const initialState: QueriesState = {
  queries: {}
};

const prepareQueryPayload = (
  type: QueryType,
  dataType: DataType,
  forceFetch = false,
  params: Params = {},
  pathParams: PathParams = {}
) => ({
  payload: { type, dataType, params, pathParams, forceFetch }
});

export const getQueryKey = (
  type: QueryType,
  dataType: DataType,
  params: Params = {},
  pathParams: PathParams = {}
) =>
  `${type}.${dataType}.${JSON.stringify(params)}.${JSON.stringify(pathParams)}`;

const getQueryKeyFromAction = (action: PayloadAction<RequestPayload>) => {
  const { type, dataType, params, pathParams } = action.payload;
  return getQueryKey(type, dataType, params, pathParams);
};

const queriesSlice = createSlice({
  name,
  initialState,
  reducers: {
    query: {
      reducer: (state: QueriesState, action: QueryAction) => {
        const queryKey = getQueryKeyFromAction(action);
        if (!state.queries[queryKey]) {
          state.queries[queryKey] = {
            isLoading: true,
            isLoaded: false
          };
        }
      },
      prepare: prepareQueryPayload
    },
    querySuccess: {
      reducer: (state: QueriesState, action: QuerySuccessAction) => {
        const queryKey = getQueryKeyFromAction(action);
        if (state.queries[queryKey]) {
          state.queries[queryKey].isLoading = false;
          state.queries[queryKey].isLoaded = true;
          state.queries[queryKey].ids = action.payload.ids;
        }
      },
      prepare: (
        type: QueryType,
        dataType: DataType,
        params: Params,
        pathParams: PathParams,
        ids: Array<string>
      ) => ({
        payload: { type, dataType, params, pathParams, ids }
      })
    },
    queryFailure: {
      reducer: (state: QueriesState, action: QueryFailureAction) => {
        const queryKey = getQueryKeyFromAction(action);
        if (state.queries[queryKey]) {
          state.queries[queryKey].isLoading = false;
          state.queries[queryKey].error = action.error;
        }
      },
      prepare: (
        type: QueryType,
        dataType: DataType,
        params: Params,
        pathParams: PathParams,
        error: AppError
      ) => ({
        payload: { type, dataType, params, pathParams },
        error
      })
    },
    reset: () => initialState
  }
});

export const { query, querySuccess, queryFailure, reset } =
  queriesSlice.actions;

const selectQueriesState = (state: { [name]: QueriesState }): QueriesState =>
  state[name];

export const selectQueries = createSelector(
  selectQueriesState,
  (state) => state.queries
);

export const selectQuery = (
  type: QueryType,
  dataType: DataType,
  params?: Params,
  pathParams?: PathParams
) =>
  createSelector(
    selectQueries,
    (queries) => queries[getQueryKey(type, dataType, params, pathParams)]
  );

export const selectQueryData = (
  type: QueryType,
  dataType: DataType,
  params?: Params,
  pathParams?: PathParams
) =>
  createSelector(
    selectQuery(type, dataType, params, pathParams),
    createSelectByIds(dataType),
    (queryState, selectByIds) =>
      queryState
        ? {
            ...queryState,
            data: selectByIds(queryState?.ids)
          }
        : undefined
  );

export default queriesSlice.reducer;
